/** Wire forms of a TimeBound.
 *
 * - QuerySearch DSL / filters AST: naive UTC `yyyy-MM-dd HH:mm:ss` (the backend reads a
 *   naive value as UTC).
 * - Toolbar URL params: ISO `yyyy-MM-ddTHH:mm:ssZ`.
 *
 * A relative bound is its token (`now-15m`) in both. Fixed bounds always carry seconds.
 * Under the end-bound rule an upper bound covers its whole last unit (`23:59:59` runs to
 * the end of that second, `23:59` to the end of that minute, a date to the end of that
 * day), so the precision written is part of the meaning, and the panel edits to the second.
 */
import { formatRelative, isRelative, parseRelative } from './relative';
import type { TimeBound } from './types';

export function boundToNaive(bound: TimeBound): string {
  return isRelative(bound)
    ? formatRelative(bound)
    : bound.toISOString().slice(0, 19).replace('T', ' ');
}

export function boundToIso(bound: TimeBound): string {
  return isRelative(bound) ? formatRelative(bound) : `${bound.toISOString().slice(0, 19)}Z`;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(\.\d{1,6})?)?(Z|[+-]\d{2}:?\d{2})?$/;

function utc(y: number, mo: number, d: number, h = 0, mi = 0, s = 0, ms = 0): Date | null {
  const t = new Date(Date.UTC(y, mo - 1, d, h, mi, s, ms));
  // Field rollover (Feb 30, month 13, hour 24) is garbage, not a date.
  const ok =
    t.getUTCFullYear() === y &&
    t.getUTCMonth() === mo - 1 &&
    t.getUTCDate() === d &&
    t.getUTCHours() === h &&
    t.getUTCMinutes() === mi &&
    t.getUTCSeconds() === s;
  return ok ? t : null;
}

/** Any bound the console has ever written, as the panel should show it: a relative
 *  token, naive UTC (minute or second precision), ISO with `Z` or an offset, or a bare
 *  date. With `end`, a coarse upper bound is shown as the last second it covers
 *  (`23:59` → 23:59:59, `2026-10-21` → 23:59:59 that day), which is what it means.
 *  Null for anything else. */
export function parseBound(
  value: string,
  { end = false }: { end?: boolean } = {},
): TimeBound | null {
  const rel = parseRelative(value);
  if (rel) return rel;

  const date = DATE_RE.exec(value);
  if (date) return utc(+date[1]!, +date[2]!, +date[3]!, end ? 23 : 0, end ? 59 : 0, end ? 59 : 0);

  const m = DATETIME_RE.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi, s, frac, zone] = m;
  const seconds = s === undefined ? (end ? 59 : 0) : +s;
  const ms = frac ? Math.floor(Number(`0${frac}`) * 1000) : 0;
  const wall = utc(+y!, +mo!, +d!, +h!, +mi!, seconds, ms);
  if (!wall || !zone || zone === 'Z') return wall;
  // ±HH:MM / ±HHMM: the wall clock is in that offset; shift to UTC.
  const sign = zone.startsWith('-') ? -1 : 1;
  const digits = zone.slice(1).replace(':', '');
  const offsetMin = sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2)));
  return new Date(wall.getTime() - offsetMin * 60_000);
}

/** A toolbar URL param → its bound (null when absent or unreadable). A hand-typed
 *  `now+2d` arrives as `now 2d` (an unencoded `+` decodes to a space) and reads as
 *  `now+2d`, as the backend reads it. */
export function paramToBound(value: string | null | undefined, { end = false }: { end?: boolean } = {}): TimeBound | null {
  return value ? parseBound(value.replace(/^now (?=\d)/, 'now+'), { end }) : null;
}

/** A bound → its toolbar URL param (null clears the param). */
export function boundToParam(bound: TimeBound | null): string | null {
  return bound ? boundToIso(bound) : null;
}
